import { SuggestionsSearchProvider } from '@nrwl/smart-search';
import { Injectable, Inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { forEach, get, isEqual, template, uniqWith } from 'lodash';

import { mostPopularQuery } from './queries/most.popular';
import { suggestionsForTermQuery } from './queries/suggestions.for.term';
import { suggestionsForTermInCategoryQuery } from './queries/suggestions.for.term.in.category';
import { suggestionsForTermInMultipleCategoriesQuery } from './queries/suggestions.for.term.multiple.cats';

const NUMBER_OF_MATCHES = 5;
@Injectable()
// TODO: use DataApiAbstraction from common-lib -> once it is ripped off olive lib
export class PtcSuggestionsSearchProvider extends SuggestionsSearchProvider {
  termSubquery = `,{ "multi_match": { "query": "<%=userInput%>", "fields":["value", "value.precise"], "operator":"and" }}`;

  userAuth: any;
  remoteUrl = `/elk/autocomplete/_search`;

  // unfortunately I can't add explicit dependency on AuthenticatedUser - this brings the whole olive-app into workflow-engine!
  constructor(
    private readonly httpClient: HttpClient,
    @Inject('userAuth') userAuth: any, //eslint-disable-line
  ) {
    super();
    this.userAuth = userAuth;
  }

  convertHitsToPairs(hits) {
    const result = [];
    for (const hit of hits ? hits : []) {
      result.push({ category: hit._source.category,
value: hit._source.value });
    }
    return result;
  }

  convertBucketsToPairs(buckets) {
    const result = [];
    forEach(buckets, bucket => {
      const value = bucket.key;
      const categoryBuckets = get(bucket, 'known_categories.buckets', []);
      forEach(categoryBuckets, category => {
        result.push({ category: category.key,
value: value });
      });
    });
    return result;
  }

  removeDuplicateRecords(pairs = []) {
    // important: we must preserve overall order of records.
    return uniqWith(pairs, isEqual);
  }

  /**
   * How it is supposed to work? First, normally we'd ask for top matched documents, sorted by score,
   * and show what we get. However, there are at least 2 cases, where this approach fails to produce
   * satisfactory results:
   * a) there are duplicate documents (eg, multiple applications with the same name)
   * b) in case user does not enter anything and wants to see most popular terms (in this case scores are 1.0, and
   *    top documents are pretty random)
   * Therefore we ask for both 'top' matches and aggregation buckets. Then they are merged like this:
   * - both hits and buckets are converted to {category,value} pairs and all is pushed to the same array, strictly
   * in the same order as returned by ES.
   * - duplicate entries from the final array are removed, and array is cut to max 5 entries
   */
  parseAggregatesResponse(hits = [], buckets = []) {
    let result = this.convertHitsToPairs(hits);
    result = result.concat(this.convertBucketsToPairs(buckets));
    result = this.removeDuplicateRecords(result);
    // and trim to avoid too many options
    return result.slice(0, NUMBER_OF_MATCHES);
  }

  // this is the query to be used when nothing is known about user intentions
  // hence we just search the db for most popular terms - whatever they are.
  public searchForMostPopular(): Observable<any> {
    const reqBody = template(mostPopularQuery)({
      maxCount: NUMBER_OF_MATCHES
    });
    return this._post(reqBody).pipe(
      map(input => {
        const hits = get(input, 'hits.hits', []);
        const buckets = get(input, 'aggregations.top_values.buckets', []);
        return this.parseAggregatesResponse(hits, buckets);
      })
    );
  }

  // this is a method to use when user types in one or  more characters. we assume
  // those characters represent 'value' without being bound to specific 'category'
  // the query should return most popular 'values' in all 'categories'.
  public searchForSuggestions(
    term: string,
    allowedCategories: string[]
  ): Observable<any> {
    if (allowedCategories && allowedCategories.length > 0) {
      return this.searchForSuggestionsByTermMultipleCategories(
        term,
        allowedCategories
      );
    }
    return this.searchForSuggestionsByTermNoCategory(term);
  }

  searchForSuggestionsByTermMultipleCategories(
    term: string,
    allowedCategories: string[]
  ): Observable<any> {
    let termQueryBody = '';
    if (term) {
      term = this.escapeBackslashInString(term);

      termQueryBody = template(this.termSubquery)({
        userInput: term
      });
    }
    const reqBody = template(suggestionsForTermInMultipleCategoriesQuery)({
      categories: `"${allowedCategories.join('","')}"`,
      termSubquery: termQueryBody,
      maxCount: NUMBER_OF_MATCHES
    });

    return this._post(reqBody).pipe(
      map(input => {
        const hits = get(input, 'hits.hits', []);
        const buckets = get(input, 'aggregations.top_values.buckets', []);
        return this.parseAggregatesResponse(hits, buckets);
      })
    );
  }

  searchForSuggestionsByTermNoCategory(term: string): Observable<any> {
    term = this.escapeBackslashInString(term);

    const reqBody = template(suggestionsForTermQuery)({
      userInput: term,
      maxCount: NUMBER_OF_MATCHES
    });
    return this._post(reqBody).pipe(
      map(input => {
        const hits = get(input, 'hits.hits', []);
        const buckets = get(
          input,
          'aggregations.top_values.by_value.buckets',
          []
        );
        return this.parseAggregatesResponse(hits, buckets);
      })
    );
  }

  prepareTermsInCategoryQuery(category: string, term?: string) {
    let termQueryBody = '';
    if (term) {
      term = this.escapeBackslashInString(term);

      termQueryBody = template(this.termSubquery)({
        userInput: term
      });
    }
    return template(suggestionsForTermInCategoryQuery)({
      category,
      termSubquery: termQueryBody,
      maxCount: NUMBER_OF_MATCHES
    });
  }

  // this is a query to use when 'category' is known. If term is not given, we shall
  // return most popular 'values' within that 'category'. If term is given,
  // then top N matching 'values' within 'category' will be returned.
  public searchForSuggestionsInCategory(
    category: string,
    term?: string
  ): Observable<any> {
    const reqBody = this.prepareTermsInCategoryQuery(category, term);
    return this._post(reqBody).pipe(
      map(input => {
        const hits = get(input, 'hits.hits', []);
        const buckets = get(input, 'aggregations.top_values.buckets', []);
        return this.parseAggregatesResponse(hits, buckets);
      })
    );
  }

  _post(reqBody) {
    return this.httpClient.post(this.remoteUrl, reqBody, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + this.userAuth.token
      }
    });
  }

  private escapeBackslashInString(str: string) {
    return (str + '').replace(/[\\"']/g, '\\$&').replace(/\u0000/g, '\\0');
    //return (str + '').replace(/[\\"']/g, '\\\\$&').replace(/\u0000/g, '\\0');
  }
}
